/**
 * The amber shell the reopened-for-revision banner wears, on both surfaces.
 *
 * Two near-identical inline style blocks lived here — one on the week row, one
 * on the rollup detail page's toolbar — differing only in padding and how the
 * children wrap. That is the shape RULES.md 3.2 hoisted `SegButton` for: two
 * copies of one thing drift apart the first time somebody adjusts only the one
 * they are looking at, and a warning surface that stops matching its twin
 * stops reading as the same kind of warning.
 *
 * DENSITY IS THE ONLY DIFFERENCE, and it is real rather than cosmetic:
 *   - `row` — a full-width strip on `.week-row-extra`'s second grid line,
 *     where it has the whole row to breathe and the content may wrap.
 *   - `toolbar` — an inline chip sharing a line with the back chip and the
 *     week's actions, where it must stay one line and take as little width as
 *     it can.
 *
 * Everything else — the amber border, the `--warning-tint` fill, the warning
 * glyph, the type size — is fixed here on purpose. A caller that wants to
 * change one of those is describing a different warning, and should say so in
 * `index.css` where the rest of the amber family lives (RULES.md 2.8).
 *
 * The children are the message; this owns only the shell and the glyph.
 */
import type { ReactNode } from 'react';

export type RevisionBannerDensity = 'row' | 'toolbar';

export function RevisionBannerChrome({
  density,
  className,
  children,
}: {
  density: RevisionBannerDensity;
  /** Grid/layout placement from the caller — `week-row-extra` on the week row,
   *  nothing on the toolbar. Deliberately not baked in: where the strip SITS
   *  is the caller's business, what it LOOKS like is this component's. */
  className?: string;
  children: ReactNode;
}) {
  const row = density === 'row';
  return (
    <div
      role="status"
      className={className}
      style={{
        display: row ? 'flex' : 'inline-flex',
        alignItems: row ? 'flex-start' : 'center',
        gap: row ? 8 : 7,
        padding: row ? '8px 10px' : '4px 9px',
        borderRadius: 6,
        border: '1px solid var(--warning)',
        background: 'var(--warning-tint)',
        fontSize: 12.5,
        lineHeight: row ? 1.45 : 1.3,
        color: 'var(--ink)',
      }}
    >
      <span aria-hidden style={{ color: 'var(--warning)', flexShrink: 0 }}>
        &#9888;
      </span>
      {children}
    </div>
  );
}
